<template>
    <v-autocomplete
        :value="value"
        :items="displayItems"
        :loading="loading"
        :label="label"
        :disabled="disabled"
        :clearable="clearable"
        :required="required"
        :multiple="multiple"
        :chips="multiple"
        :deletable-chips="multiple"
        :small-chips="multiple"
        item-text="text"
        item-value="value"
        :prepend-inner-icon="icon ? iconName(icon) : null"
        :dense="dense"
        :outlined="outlined"
        :flat="flat"
        :hide-details="hideDetails"
        :background-color="backgroundColor"
        :menu-props="{ closeOnContentClick: !multiple }"
        @input="onInput"
    />
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

export default {
    name: "MetadataSearch",
    mixins: [GlobalMixins],
    props: {
        value: {
            type: [String, Number, Array],
            default: null,
        },
        label: {
            type: String,
            default: "",
        },
        field: {
            type: String,
            default: "manufacturer",
        },
        endpoint: {
            type: String,
            default: "/api/productsmetadata/",
        },
        additionalParams: {
            type: Object,
            default: () => ({}),
        },
        unit: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        required: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        outlined: {
            type: Boolean,
            default: true,
        },
        flat: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: [Boolean, String],
            default: "auto",
        },
        backgroundColor: {
            type: String,
            default: null,
        },
        icon: {
            type: String,
            default: "manufacturer",
        },
        limit: {
            type: Number,
            default: 10000,
        },
    },
    data() {
        return {
            loading: false,
            rawItems: [],
        };
    },
    computed: {
        normalizedItems() {
            return (this.rawItems || []).map((item) => {
                const base = item.text || item.value || "";
                const text = this.unit ? `${base} ${this.unit}`.trim() : base;
                return {
                    text,
                    value: item.value,
                };
            });
        },
        displayItems() {
            const base = this.normalizedItems;
            if (this.multiple) {
                const selected = Array.isArray(this.value) ? this.value : [];
                const missing = selected
                    .filter((v) => v != null && v !== "" && !base.some((i) => i.value === v))
                    .map((v) => ({ text: String(v), value: v }));
                return missing.length ? [...missing, ...base] : base;
            }
            const v = this.value;
            if (v != null && v !== "" && !base.some((i) => i.value === v)) {
                return [{ text: String(v), value: v }, ...base];
            }
            return base;
        },
    },
    watch: {
        additionalParams: {
            handler() {
                this.fetchValues();
            },
            deep: true,
        },
        endpoint() {
            this.fetchValues();
        },
        field() {
            this.fetchValues();
        },
    },
    mounted() {
        this.fetchValues();
    },
    methods: {
        onInput(v) {
            if (this.multiple) {
                const arr = Array.isArray(v) ? v.filter((x) => x != null && x !== "") : [];
                this.$emit("input", arr.length ? arr : null);
                return;
            }
            this.$emit("input", v != null && v !== "" ? v : null);
        },
        async fetchValues() {
            this.loading = true;
            try {
                const params = {
                    field: this.field,
                    limit: this.limit,
                    ...this.additionalParams,
                };
                const { data } = await axios.get(this.endpoint, { params });
                this.rawItems = data.values || [];
            } catch (e) {
                console.error(e);
                this.rawItems = [];
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
